import { createReducer, on } from '@ngrx/store';

import {
    bulkGrantPermission,
    bulkGrantPermissionError,
    bulkGrantPermissionSuccess,
    bulkRemovePermission,
    bulkRemovePermissionError,
    bulkRemovePermissionSuccess,
    grantPermissionError,
    grantPermissionSuccess,
    removePermission,
    removePermissionError,
    removePermissionSuccess,
} from '../actions/access-permissions.actions';
import {
    resetAddUserStepperUserSelection,
    updateAddUserStepperAddedUser,
    updateAddUserStepperSelectedAccess,
    updateAddUserStepperSelectedAccounts,
    updateAddUserStepperSelectedUser,
    updateAddUserStepperSelectedUserType,
} from '../actions/add-user-stepper-flow.action';
import { UserTypeSelection } from '../../enums/user-type-selection.enum';
import {
    addNewUser,
    incrementSearchPersonAttemptCounter,
    resetGrantRevokePermissionState,
    resetGrantRevokePermissionStateExceptAttemptCounter,
    resetSearchPersonAttemptCounter,
} from '../actions/grant-revoke-permissions.action';
import {
    ineligibleScenariosErrorForGrantee,
    ineligibleScenariosForGrantee,
    ineligibleScenariosSuccessForGrantee,
} from '../actions/ineligible-scenarios.action';
import {
    updateRemoveUserStepperSelectedAccount,
    updateRemoveUserStepperSelectedAccounts,
    updateRemoveUserStepperSelectedUser,
} from '../actions/remove-user-stepper-flow.action';
import { GrantRevokePermissionsInitialState } from '../state/grant-revoke-permissions-initial.state';
import {
    gatherPinAndDto,
    gatherPinAndDtoError,
    gatherPinAndDtoMultiAccount,
    gatherPinAndDtoMultiAccountError,
    gatherPinAndDtoMultiAccountSuccess,
    gatherPinAndDtoSuccess,
} from '../actions/add-someone-else.action';

export const grantRevokePermissionsReducer = createReducer(
    GrantRevokePermissionsInitialState,

    on(updateAddUserStepperSelectedUserType, (state, { selectedUserType }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            addUser: {
                ...state.accessFlowStepper.addUser,
                selectedUserType,
            },
        },
    })),

    on(updateAddUserStepperSelectedUser, (state, { selectedUser }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            addUser: {
                ...state.accessFlowStepper.addUser,
                selectedUser: selectedUser,
                selectedAccounts: [],
            },
        },
    })),

    on(resetAddUserStepperUserSelection, (state) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            addUser: {
                ...state.accessFlowStepper.addUser,
                selectedUserType: UserTypeSelection.EXISTING,
                selectedUser: null,
            },
        },
    })),

    on(updateAddUserStepperAddedUser, (state, { addedUser }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            addUser: {
                ...state.accessFlowStepper.addUser,
                addedUser: {
                    data: addedUser,
                    isLoading: false,
                    error: null,
                    hasLoaded: true,
                },
            },
        },
    })),

    on(updateAddUserStepperSelectedAccess, (state, { selectedAccess }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            addUser: {
                ...state.accessFlowStepper.addUser,
                selectedAccess,
            },
        },
    })),

    on(updateAddUserStepperSelectedAccounts, (state, { selectedAccounts }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            addUser: {
                ...state.accessFlowStepper.addUser,
                selectedAccounts,
            },
        },
    })),

    on(resetGrantRevokePermissionState, () => GrantRevokePermissionsInitialState),

    on(resetGrantRevokePermissionStateExceptAttemptCounter, (state) => {
        return {
            ...GrantRevokePermissionsInitialState,
            accessFlowStepper: {
                ...GrantRevokePermissionsInitialState.accessFlowStepper,
                addUser: {
                    ...GrantRevokePermissionsInitialState.accessFlowStepper.addUser,
                    searchPersonAttemptCounter:
                        state?.accessFlowStepper?.addUser?.searchPersonAttemptCounter,
                },
                operationResponse:
                    GrantRevokePermissionsInitialState.accessFlowStepper.operationResponse,
            },
        };
    }),

    on(incrementSearchPersonAttemptCounter, (state) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            addUser: {
                ...state.accessFlowStepper.addUser,
                searchPersonAttemptCounter:
                    /* istanbul ignore next */
                    state?.accessFlowStepper?.addUser?.searchPersonAttemptCounter + 1,
            },
        },
    })),

    on(resetSearchPersonAttemptCounter, (state) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            addUser: {
                ...state.accessFlowStepper.addUser,
                searchPersonAttemptCounter: 0,
            },
        },
    })),

    on(gatherPinAndDto, (state) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: true,
        },
    })),

    on(gatherPinAndDtoError, (state, { error }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error,
        },
    })),

    on(gatherPinAndDtoSuccess, (state, { data }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            uniquePin: data,
        },
    })),

    on(gatherPinAndDtoMultiAccount, (state) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: true,
        },
    })),

    on(gatherPinAndDtoMultiAccountSuccess, (state, { data }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            operationResponse: data,
            uniquePin: data.uniquePin,
        },
    })),

    on(gatherPinAndDtoMultiAccountError, (state, { error }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error,
        },
    })),

    on(addNewUser, (state, { addedUser }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            addUser: {
                ...state.accessFlowStepper.addUser,
                addedUser,
            },
        },
    })),

    on(grantPermissionSuccess, (state, { data }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error: null,
            operationResponse: { results: [data] },
        },
    })),

    on(grantPermissionError, (state, { error }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error,
        },
    })),

    on(ineligibleScenariosForGrantee, (state) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: true,
            error: null,
        },
    })),

    on(ineligibleScenariosSuccessForGrantee, (state, { granteeIneligibleReason, userDetails }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error: null,
            addUser: {
                ...state.accessFlowStepper.addUser,
                selectedUser: {
                    ...userDetails,
                    granteeIneligibleReason,
                },
            },
        },
    })),

    on(ineligibleScenariosErrorForGrantee, (state, { error }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error,
        },
    })),

    on(updateRemoveUserStepperSelectedAccount, (state, { selectedAccount }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            removeUser: {
                ...state.accessFlowStepper.removeUser,
                selectedAccounts: [selectedAccount],
            },
        },
    })),

    on(updateRemoveUserStepperSelectedAccounts, (state, { selectedAccounts }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            removeUser: {
                ...state.accessFlowStepper.removeUser,
                selectedAccounts,
            },
        },
    })),

    on(updateRemoveUserStepperSelectedUser, (state, { selectedUser }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            removeUser: {
                ...state.accessFlowStepper.removeUser,
                selectedUser: selectedUser,
            },
        },
    })),

    on(removePermission, (state) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: true,
            error: null,
        },
    })),
    on(removePermissionSuccess, (state, { data }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error: null,
        },
    })),

    on(removePermissionError, (state, { error }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error,
        },
    })),

    on(bulkGrantPermission, (state) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: true,
            error: null,
        },
    })),

    on(bulkGrantPermissionSuccess, (state, { data }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error: null,
            operationResponse: data,
        },
    })),

    on(bulkGrantPermissionError, (state, { error }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error,
        },
    })),

    on(bulkRemovePermission, (state) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: true,
            error: null,
        },
    })),

    on(bulkRemovePermissionSuccess, (state, { data }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error: null,
            operationResponse: data,
        },
    })),

    on(bulkRemovePermissionError, (state, { error }) => ({
        ...state,
        accessFlowStepper: {
            ...state.accessFlowStepper,
            isLoading: false,
            error,
        },
    })),
);
